import { useCallback } from "react"

import { useNavigate } from "react-router-dom"

import type { WorkflowItem } from "@/api/models"

import { Play, TestTube2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"
import { usePendingWorkflowStore } from "@/stores/pending-workflow-store"

type ExpandableRunButtonsProps = {
  workflowId: string
  workflow: WorkflowItem
  isExpanded: boolean
  onExpand: () => void
  runLabel: string
  testLabel: string
  explorerLabel: string
}

export function ExpandableRunButtons({
  workflowId,
  workflow,
  isExpanded,
  onExpand,
  runLabel,
  testLabel,
  explorerLabel,
}: ExpandableRunButtonsProps) {
  const navigate = useNavigate()
  const setPendingWorkflow = usePendingWorkflowStore((state) => state.setPendingWorkflow)

  const handleTestInEditor = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      void navigate(`/flows/${workflowId}?test=true`)
    },
    [navigate, workflowId]
  )

  const handleRunInExplorer = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setPendingWorkflow(workflow)
      void navigate("/explorer")
    },
    [navigate, workflow, setPendingWorkflow]
  )

  return (
    <div className="relative flex items-center">
      {/* Collapsed: Run button */}
      <Button
        size="sm"
        className={cn(
          "h-7 gap-1 px-2.5 text-xs font-medium",
          "transition-[opacity,scale,box-shadow] duration-150 ease-out",
          "hover:scale-105 hover:shadow-md",
          isExpanded ? "pointer-events-none absolute left-0 opacity-0" : "opacity-100"
        )}
        onClick={(e) => {
          e.stopPropagation()
          onExpand()
        }}
      >
        <Play className="h-3 w-3" />
        {runLabel}
      </Button>

      {/* Expanded: Two action buttons */}
      <div
        className={cn(
          "flex items-center gap-1.5",
          "transition-opacity duration-150 ease-out",
          isExpanded ? "opacity-100" : "pointer-events-none absolute left-0 opacity-0"
        )}
      >
        <Button
          className="hover:shadow-primary/25 h-7 gap-1.5 px-2.5 text-xs font-medium whitespace-nowrap transition-[scale,box-shadow] duration-150 hover:scale-[1.02] hover:shadow-md"
          size="sm"
          onClick={handleRunInExplorer}
        >
          <Play className="h-3.5 w-3.5 shrink-0" />
          {explorerLabel}
        </Button>

        <Button
          size="sm"
          className={cn(
            "h-7 gap-1.5 px-2.5 text-xs font-medium whitespace-nowrap transition-[background-color,scale] duration-150",
            STATUS_TONES.success.soft,
            "hover:bg-success/25 hover:scale-[1.02]"
          )}
          onClick={handleTestInEditor}
        >
          <TestTube2 className="h-3.5 w-3.5 shrink-0" />
          {testLabel}
        </Button>
      </div>
    </div>
  )
}
